import { useMemo, useState } from "react";
import useAdminQuery from "../../lib/queries/useAdminQuery";
import { Topbar } from "../../components/AppShell";
import { LoadingPanel } from "../../components/LoadingPanel";
import { SIGNUP_FUNNEL_V2_SQL, mapRows } from "./sql";
import type { UserRowV2 } from "./sql";
import { FiltersBar, applyFilters, filtersToPills } from "../../lib/funnel/filters";
import type { Filters } from "../../lib/funnel/filters";
import { KpiCard, EmptyState } from "../../lib/funnel/charts";
import { fmtN, fmtPct, groupCountry, previousMonth } from "../../lib/funnel/helpers";
import { STAGES, medianDaysToStage, HOURLY_MAX_DAYS, FIFTEEN_MIN_MAX_DAYS } from "./helpers";
import type { Granularity, StageKey } from "./helpers";

const OBSERVATION_OPTIONS = [1, 7, 30, 90, 180];
import { ButterflyChartV2 } from "./ButterflyChartV2";
import { StageHistograms } from "./StageHistograms";
import { StepDropoff } from "./StepDropoff";
import { TimeToPaidPanel } from "./TimeToPaidPanel";
import { CohortLab } from "./CohortLab";
import type { FilterPillSpec } from "../../components/FilterPills";

function dedupeByUser(rows: UserRowV2[]): UserRowV2[] {
  const seen = new Map<string, UserRowV2>();
  for (const r of rows) {
    const existing = seen.get(r.user_external_id);
    if (!existing) { seen.set(r.user_external_id, r); continue; }
    // Prefer the row with the most milestone hits, then paid plan, then earliest signup.
    const score = (u: UserRowV2): number => {
      let s = 0;
      for (const st of STAGES) if (st.dateOf(u)) s += 1;
      if (u.on_paid_plan) s += 10;
      return s;
    };
    if (score(r) > score(existing)) seen.set(r.user_external_id, r);
  }
  return Array.from(seen.values());
}

export function SignupFunnelV2() {
  const [filters, setFilters] = useState<Filters>(() => ({
    months: [previousMonth()],
    countries: [],
    roles: [],
    companySizes: [],
    excludeSupport: true,
    granularity: "month",
    cohortDayFrom: null,
    cohortDayTo: null,
    demoBucketing: "signup_date",
    observationDays: 90,
  }));
  const [granularity, setGranularity] = useState<Granularity>("day");
  const [selectedStages, setSelectedStages] = useState<StageKey[]>(() => STAGES.map((s) => s.key));
  const observationDays = filters.observationDays ?? 90;

  // If the window expands past the cap for a finer granularity, fall back
  // to the next-coarsest sensible bucket so we don't try to render
  // thousands of bars per tile.
  const effectiveGranularity: Granularity =
    granularity === "15min" && observationDays > FIFTEEN_MIN_MAX_DAYS
      ? (observationDays <= HOURLY_MAX_DAYS ? "hour" : "day")
      : granularity === "hour" && observationDays > HOURLY_MAX_DAYS
        ? "day"
        : granularity;

  const setObservationDays = (d: number) => setFilters((f) => ({ ...f, observationDays: d }));

  const result = useAdminQuery(SIGNUP_FUNNEL_V2_SQL);

  const rawPairRows = useMemo<UserRowV2[]>(() => {
    if (!result.data) return [];
    return mapRows(result.data.columns, result.data.rows as unknown[][]);
  }, [result.data]);

  const availableCountries = useMemo(() => {
    const set = new Set<string>();
    rawPairRows.forEach((u) => set.add(groupCountry(u.country_code)));
    return Array.from(set);
  }, [rawPairRows]);

  const availableRoles = useMemo(() => {
    const set = new Set<string>();
    rawPairRows.forEach((u) => set.add(u.user_role ?? "unknown"));
    return Array.from(set).sort();
  }, [rawPairRows]);

  const filteredPairRows = useMemo(() => applyFilters(rawPairRows, filters), [rawPairRows, filters]);
  const distinctUsers = useMemo(() => dedupeByUser(filteredPairRows), [filteredPairRows]);

  const pagePills = useMemo<FilterPillSpec[]>(() => filtersToPills(filters), [filters]);

  return (
    <>
      <Topbar crumbs={["Dashboards", "Sign-up Activation v2"]} />
      <div className="page">
        <div className="page-header">
          <div>
            <div className="eyebrow">Hidden preview, not linked from sidebar</div>
            <h1 style={{ margin: 0 }}>Sign-up Activation v2</h1>
            <p className="lede">
              Milestone-timing view: how long after signing up do users hit each activation step,
              and where do they fall out? Cohort filters at the top scope by sign-up date; the
              histograms re-cast every user onto a shared days-since-signup x-axis (window selectable below).
            </p>
          </div>
        </div>

        <FiltersBar
          filters={filters}
          onChange={setFilters}
          availableCountries={availableCountries}
          availableRoles={availableRoles}
          usersInView={distinctUsers.length}
          cohortVariant="single-with-day"
          observationWindowOptions={OBSERVATION_OPTIONS}
        />

        {result.isLoading ? (
          <LoadingPanel />
        ) : result.isError ? (
          <p style={{ color: "var(--fg-danger)" }}>{(result.error as Error).message}</p>
        ) : rawPairRows.length === 0 ? (
          <EmptyState message="No users match this cohort window." />
        ) : distinctUsers.length === 0 ? (
          <EmptyState message="No users match your filter combination. Try widening the filters." />
        ) : (
          <>
            <CohortLab
              rows={rawPairRows}
              availableCountries={availableCountries}
              availableRoles={availableRoles}
            />
            <KpiRow users={distinctUsers} />
            <ButterflyChartV2 users={distinctUsers} pagePills={pagePills} />
            <StageHistograms
              users={distinctUsers}
              granularity={effectiveGranularity}
              onGranularityChange={setGranularity}
              observationDays={observationDays}
              observationOptions={OBSERVATION_OPTIONS}
              onObservationDaysChange={setObservationDays}
              selectedStages={selectedStages}
              onSelectedStagesChange={setSelectedStages}
              pagePills={pagePills}
            />
            <StepDropoff users={distinctUsers} pagePills={pagePills} />
            <TimeToPaidPanel users={distinctUsers} pagePills={pagePills} />
          </>
        )}
      </div>
    </>
  );
}

function KpiRow({ users }: { users: UserRowV2[] }) {
  const total = users.length;
  const paid = users.filter((u) => u.on_paid_plan).length;
  const synopsis = users.filter((u) => u.first_synopsis_at).length;
  const secondSynopsis = users.filter((u) => u.second_synopsis_at).length;
  const askMarloo = users.filter((u) => u.first_meeting_chat_at).length;
  const medSynopsis = medianDaysToStage(users, STAGES.find((s) => s.key === "first_synopsis")!);
  const medAskMarloo = medianDaysToStage(users, STAGES.find((s) => s.key === "first_chat")!);
  const medPaid = medianDaysToStage(users, STAGES.find((s) => s.key === "paid")!);
  return (
    <div className="kpi-grid">
      <KpiCard
        label="Sign-ups in cohort"
        value={fmtN(total)}
        info="Distinct users after applying every filter. Each user is counted once even if active in multiple workspaces."
      />
      <KpiCard
        label="Reached 1st summary"
        value={fmtPct(synopsis, total)}
        sub={`${fmtN(synopsis)} / ${fmtN(total)}${medSynopsis != null ? ` · median ${medSynopsis}d` : ""}`}
        tone="primary"
        info="Lifetime: had at least one non-demo summary in any workspace."
      />
      <KpiCard
        label="Reached 2nd summary"
        value={fmtPct(secondSynopsis, total)}
        sub={`${fmtN(secondSynopsis)} / ${fmtN(total)}`}
        tone="primary"
        info="Two distinct non-demo meetings each with at least one summary."
      />
      <KpiCard
        label="Used Ask Marloo in a meeting"
        value={fmtPct(askMarloo, total)}
        sub={`${fmtN(askMarloo)} / ${fmtN(total)}${medAskMarloo != null ? ` · median ${medAskMarloo}d` : ""}`}
        tone="primary"
        info="Sent at least one Ask Marloo chat scoped to a non-demo, non-deleted meeting (directly or via its summary)."
      />
      <KpiCard
        label="Converted to paid"
        value={fmtPct(paid, total)}
        sub={`${fmtN(paid)} / ${fmtN(total)}${medPaid != null ? ` · median ${medPaid}d` : ""}`}
        tone="success"
        info="Workspace is on a Plus or Pro plan. Includes users who joined an already-paid workspace."
      />
    </div>
  );
}
